import { Profile } from '@src/components/guard/CommunityGuard';
import PaginationComponent from '@src/components/Pagination';
import { ChannelName } from '@src/components/ui/ChannelName';
import { ChatSearchSortOrder } from '@src/enum/chatSearchSortOrder';
import {
  SearchEmptyState,
  SearchResultCard,
} from '@src/features/shared/community/components/search';
import CommunityChatSearchHeader from '@src/features/shared/community/components/search/CommunityChatSearchHeader';
import SearchResultSummary from '@src/features/shared/community/components/search/SearchResultSummary';
import { useChatSearch } from '@src/features/shared/community/hooks/useChatSearch';
import { useAccessibleChannels } from '@src/hooks/useAccessibleChannels';
import { SHARED_URL } from '@src/routes/shared';
import { loadChannels } from '@src/store/channelSlice';
import { AppDispatch, RootState } from '@src/store/store';
import DropdownCheckbox from '@src/ui/elements/form/DropdownCheckbox';
import Select from '@src/ui/elements/form/Select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';

interface CommunityChatSearchFormProps {
  profile?: Profile;
}
interface ChatSearchResult {
  id: number;
  parent_chat_id?: number;
  message: string;
  channel: {
    code: string;
    name: string;
  };
}

function CommunityChatSearchForm({ profile }: CommunityChatSearchFormProps) {
  const dispatch = useDispatch<AppDispatch>();
  const { channels } = useSelector((state: RootState) => state.channel);
  const isInitialMountRef = useRef(true);
  const [localSelectedChannels, setLocalSelectedChannels] = useState<string[]>([]);

  const { accessibleChannels } = useAccessibleChannels(profile?.isLogin);

  const accessibleChannelCodes = useMemo(() => {
    if (!accessibleChannels?.channel) {
      return [];
    }
    return [...accessibleChannels.channel.public, ...accessibleChannels.channel.not_public];
  }, [accessibleChannels]);

  const availableChannels = useMemo(() => {
    if (!accessibleChannelCodes.length || !channels.length) {
      return [];
    }

    return channels.filter((channel) => accessibleChannelCodes.includes(channel.code));
  }, [channels, accessibleChannelCodes]);

  const {
    searchValue,
    setSearchValue,
    keyword,
    searchResults,
    hasResults,
    resultCount,
    isLoading,
    pagination,
    selectedChannels,
    sortOrder,
    handleSearch,
    clearSearch,
    handlePageChange,
    handleChannelSelection,
    handleSortOrderChange,
  } = useChatSearch();

  const navigate = useNavigate();

  useEffect(() => {
    if (channels.length === 0) {
      dispatch(loadChannels());
    }
  }, [dispatch, channels.length]);

  useEffect(() => {
    setLocalSelectedChannels(selectedChannels);
  }, [selectedChannels]);

  useEffect(() => {
    if (
      availableChannels.length > 0 &&
      selectedChannels.length === 0 &&
      isInitialMountRef.current
    ) {
      const allChannelCodes = availableChannels.map((channel) => channel.code);
      setLocalSelectedChannels(allChannelCodes);
      handleChannelSelection(allChannelCodes);
      isInitialMountRef.current = false;
    }
  }, [availableChannels, selectedChannels.length, handleChannelSelection]);

  const handleChannelSelectionWrapper = useCallback(
    (channelCodes: string[]) => {
      setLocalSelectedChannels(channelCodes);
      handleChannelSelection(channelCodes);
    },
    [handleChannelSelection],
  );

  const channelOptions = useMemo(() => {
    const allChannelsOption = {
      label: 'すべてのチャンネル',
      value: 'all',
    };

    const channelItems = availableChannels.map((channel) => ({
      label: (
        <ChannelName
          name={channel.name}
          emoji={channel.emoji}
          iconColor='#6B7280'
          textClassName='text-sm text-gray-700'
          iconSize={16}
          visibility={channel.visibility}
          showLockIcon={true}
        />
      ),
      value: channel.code,
    }));

    return [allChannelsOption, ...channelItems];
  }, [availableChannels]);

  const initialSelectedChannelOptions = useMemo(() => {
    if (localSelectedChannels.length === 0) {
      return [];
    }

    if (localSelectedChannels.length === availableChannels.length) {
      return channelOptions;
    }

    return channelOptions.filter(
      (option) => option.value !== 'all' && localSelectedChannels.includes(option.value),
    );
  }, [channelOptions, localSelectedChannels, availableChannels.length]);

  const sortOrderOptions = [
    { id: ChatSearchSortOrder.NEWEST, name: '新着順' },
    { id: ChatSearchSortOrder.OLDEST, name: '投稿日順' },
  ];

  const handleSortChange = (selectedId: string) => {
    handleSortOrderChange(selectedId as ChatSearchSortOrder);
  };

  const handleResultClick = (result: ChatSearchResult) => {
    const basePath = generatePath(SHARED_URL.COMMUNITY_CHANNEL, { code: result.channel.code });
    const searchParams = new URLSearchParams();
    searchParams.set('chatId', result.id.toString());
    if (result.parent_chat_id) {
      searchParams.set('force_thread_display', 'true');
    }
    navigate(`${basePath}?${searchParams.toString()}`);
  };

  const handleHistoryItemSelect = (selectedItem?: string) => {
    if (selectedItem) {
      setSearchValue(selectedItem);
    }
    handleSearch(selectedItem);
  };

  return (
    <main className='flex h-full flex-col lg:h-[calc(100dvh-60px)]'>
      <CommunityChatSearchHeader
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        keyword={keyword}
        handleSearch={handleSearch}
        clearSearch={clearSearch}
        onHistoryItemSelect={handleHistoryItemSelect}
      />
      <div className='flex flex-1 flex-col bg-gray-10 pb-6 pl-6 pr-[41px] pt-2 sm:pl-5 sm:pr-3'>
        <div className='flex w-full justify-between md:gap-[7px]'>
          <div className='flex items-center justify-between gap-3 sm:w-3/6 sm:gap-[7px] xs:w-1/2'>
            <div className='w-[199px] sm:w-full xs:w-full'>
              <DropdownCheckbox
                key='community-channel-filter'
                className='h-[42px] !rounded-[4px] !px-6 !pr-3 text-sm font-medium sm:!pl-5 sm:!pr-6 xs:text-xs'
                options={channelOptions}
                onSelect={(selected) => {
                  const selectedValues = selected.map((option) => option.value);
                  const channelCodes = selectedValues.filter((value) => value !== 'all');
                  handleChannelSelectionWrapper(channelCodes);
                }}
                placeholder='チャンネルを選択'
                allSelectedText='すべてのチャンネル'
                selectedCountText={(count) => `${count}件のチャンネル`}
                menuWidth={199}
                dropdownWidth={270}
                initialSelectedOptions={initialSelectedChannelOptions}
              />
            </div>
            <SearchResultSummary
              keyword={keyword}
              resultCount={resultCount}
              className='md:hidden'
            />
          </div>
          <div className='flex items-center gap-3 lg:gap-[7px] sm:w-3/6 xs:w-1/2'>
            <span className='text-[10px] sm:w-5'>並べ替え</span>
            <Select
              items={sortOrderOptions}
              initialValue={sortOrder}
              onSelect={handleSortChange}
              containerClassName='sm:w-[calc(100%_-_1.25rem_-_7px)]'
              className='h-[42px] w-[160px] rounded-[4px] !pl-4 !pr-[50px] text-sm font-medium sm:w-full sm:!pl-5 sm:!pr-6 xs:text-xs'
              optionClassName='lg:text-xs'
              showPlaceholderOption={false}
            />
          </div>
        </div>

        <SearchResultSummary
          keyword={keyword}
          resultCount={resultCount}
          className='mt-2 hidden md:block'
        />

        {/* Loading State */}
        {isLoading && (
          <div className='flex h-full items-center justify-center py-8'>
            <div className='size-8 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600'></div>
          </div>
        )}

        {/* Search Results */}
        {!isLoading && hasResults && (
          <div className='mb-6 mt-4 flex flex-col gap-4'>
            {searchResults.map((result) => (
              <SearchResultCard
                key={result.id}
                result={result}
                keyword={keyword}
                onResultClick={handleResultClick}
              />
            ))}
          </div>
        )}

        {(!hasResults || !keyword) && !isLoading && <SearchEmptyState />}

        {/* Pagination */}
        {!isLoading && pagination && hasResults && (
          <div className='my-6 flex justify-center'>
            <PaginationComponent
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </main>
  );
}

export default CommunityChatSearchForm;
